import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Grid, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import FixedSnackbar from './FixedSnackbar';

export default function VacationRequest({ userEmail }) {
  const { t } = useTranslation();
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showForm, setShowForm] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vacType, setVacType] = useState('vacation');
  const [reason, setReason] = useState('');

  const fetchVacations = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.VACATION_GET(userEmail), { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVacations(data.vacations || []);
      }
    } catch (err) {
      console.error('Error fetching vacations:', err);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => { fetchVacations(); }, [fetchVacations]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) return;
    setSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.VACATION_SUBMIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ start_date: startDate, end_date: endDate, type: vacType, reason })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'vacation_error_submit');
      }
      setSnackbar({ open: true, message: t('vacation_submitted'), severity: 'success' });
      setShowForm(false);
      setStartDate('');
      setEndDate('');
      setVacType('vacation');
      setReason('');
      fetchVacations();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('vacation_error_submit'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = { pending: 'warning', approved: 'success', rejected: 'error' };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('vacation_my_requests')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(!showForm)}
          size="small"
        >
          {t('vacation_request_new')}
        </Button>
      </Box>

      {showForm && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('vacation_type')}</InputLabel>
                <Select value={vacType} label={t('vacation_type')} onChange={(e) => setVacType(e.target.value)}>
                  <MenuItem value="vacation">{t('vacation_type_vacation')}</MenuItem>
                  <MenuItem value="day_off">{t('vacation_type_day_off')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('vacation_start_date')}
                type="date"
                size="small"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('vacation_end_date')}
                type="date"
                size="small"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate || undefined }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('vacation_reason')}
                size="small"
                fullWidth
                multiline
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !startDate || !endDate}
                startIcon={submitting ? <CircularProgress size={18} /> : undefined}
              >
                {t('vacation_submit')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
      ) : vacations.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography color="text.secondary">{t('vacation_no_requests')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('vacation_type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('vacation_start_date')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('vacation_end_date')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{t('status')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('calendar_notes_label')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vacations.map((v) => (
                <React.Fragment key={v._id}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === v._id ? null : v._id)}>
                    <TableCell>
                      {v.type === 'vacation' ? t('vacation_type_vacation') : t('vacation_type_day_off')}
                    </TableCell>
                    <TableCell>{formatDate(v.start_date)}</TableCell>
                    <TableCell>{formatDate(v.end_date)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={t(`vacation_status_${v.status}`)}
                        color={statusColor[v.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small">
                        {expanded === v._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0 }}>
                      <Collapse in={expanded === v._id}>
                        <Box sx={{ py: 2, px: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {t('vacation_days_count', { n: v.days_count })}
                          </Typography>
                          {v.reason && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {t('vacation_reason')}: {v.reason}
                            </Typography>
                          )}
                          {v.status === 'rejected' && v.rejection_reason && (
                            <Typography variant="body2" color="error">
                              {t('calendar_rejection_reason')}: {v.rejection_reason}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={4000}
      />
    </Box>
  );
}
